import ProfilePicture from 'components/elements/ProfilePicture'
import UserProfileDropDown from 'components/modules/Homepage/UserProfileDropDown'
import { getSidebarItems, isAppPageActive } from 'lib/constants/webappNavigation'
import { getCountryConfig } from 'lib/constants/countryconfig'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { parseLocale } from 'lib/constants/localization'
import useMedia from 'lib/hooks/useMedia'
import _useTranslation from 'lib/hooks/_useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { Body1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import Stack from 'components/elements/Stack'
import { appEditProfileRoute } from 'lib/constants/routes'
import { Tooltip } from 'components/elements/Inputs/Input.css'

const Container = styled.nav`
    padding: 25px 10px 100px 10px;
    display: grid;
    gap: 10px;
    max-height: 100vh;
    overflow: visible;
    @media (max-width: 1250px) {
        gap: 25px;
    }
    @media (max-height: 675px) {
        gap: 20px;
        padding: 10px 10px 100px 10px;
        overflow-x: visible;
    }
    svg path {
        stroke: ${theme.colors.grayScaleSecondary};
    }
`

const ClickableMenuItem = styled.div<{ isActive: boolean }>`
    display: grid;
    gap: 20px;
    grid-template-columns: 24px 1fr;
    padding: 0 10px;
    align-items: center;
    height: 56px;
    border-radius: ${theme.borderRadius.normal};
    &:hover {
        background: ${theme.colors.backgroundTransparent};
    }
    ${({ isActive }) =>
        isActive &&
        css`
            border: 1px solid ${theme.colors.grayScaleSecondary};
            background: ${theme.colors.backgroundTransparent};
        `}
    cursor: pointer;
    p {
        position: relative;
        left: 4px;
    }
    div {
        position: relative;
        left: 7px;
    }
    svg {
        position: relative;
        left: 4px;
    }
    @media (max-width: 1250px) {
        display: flex;
        justify-content: center;
        img {
            position: relative;
            left: -5px;
        }
    }
`

const MenuItem = styled(Link)<{ isActive: boolean }>`
    display: grid;
    gap: 20px;
    grid-template-columns: 24px 1fr;
    align-items: center;
    height: 56px;
    border: 1px solid transparent;

    padding: 0 10px;
    border-radius: ${theme.borderRadius.normal};
    &:hover {
        background: ${theme.colors.backgroundTransparent};
    }
    ${({ isActive }) =>
        isActive &&
        css`
            border: 1px solid ${theme.colors.grayScaleSecondary};
            background: ${theme.colors.backgroundTransparent};
        `}
    svg {
        position: relative;
        left: 4px;
    }

    @media (max-width: 1250px) {
        display: flex;
        justify-content: center;
        svg {
            position: relative;
            left: 0px;
        }
    }
`

const MenuNewTabItem = styled.a<{ isActive: boolean }>`
    display: grid;
    gap: 20px;
    grid-template-columns: 24px 1fr;
    align-items: center;
    position: relative;
    height: 56px;
    border: 1px solid transparent;
    border-radius: ${theme.borderRadius.normal};
    padding: 0 10px;
    svg {
        position: relative;
        left: 4px;
    }
    &:hover {
        //border: 1px solid ${theme.colors.grayScaleSecondary};
        background: ${theme.colors.backgroundTransparent};
    }
    ${({ isActive }) =>
        isActive &&
        css`
            border: 1px solid ${theme.colors.grayScaleSecondary};
            background: ${theme.colors.backgroundTransparent};
        `}

    @media (max-width: 1250px) {
        display: flex;

        justify-content: center;
        svg {
            position: relative;
            left: 0px;
        }
    }
`

const MenuSpacer = styled(Stack)`
    gap: 65px;
    @media (max-height: 675px) {
        gap: 20px;
    }
`

const SidebarNavigation = () => {
    const [showUserDropdown, setShowUserDropdown] = useState(false)
    const router = useRouter()
    const { locale, asPath, pathname } = router
    const parsedLocale = parseLocale(locale)
    const countryConfig = getCountryConfig(parsedLocale.countryCode)
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const authenticatedKnower = useSelector((state: AppState) => state.knower)

    const { t: tCommon } = _useTranslation('common')

    const sidebarItems = getSidebarItems(countryConfig, tCommon, pathname, authenticatedUser, !!authenticatedKnower)

    const userDropdownRef = useRef<HTMLLIElement>(null)

    const toggleUserDropdown = () => setShowUserDropdown(!showUserDropdown)

    const lg = useMedia(deviceSize.tablet)

    const handleMouseDown = (event: MouseEvent) => {
        const isClickOutsideUserDropDown = !userDropdownRef.current?.contains(event.target as Node)

        if (isClickOutsideUserDropDown) {
            setShowUserDropdown(false)
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleMouseDown)

        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
        }
    }, [])

    const LinkWrapper = (props: { openInNewTab?: boolean; children: ReactNode; href: string; isActive: boolean }) =>
        props.openInNewTab ? (
            <span>
                <MenuNewTabItem role="link" href={props.href} target="_blank" isActive={props.isActive}>
                    {props.children}
                </MenuNewTabItem>
            </span>
        ) : (
            <MenuItem role="link" href={props.href} isActive={props.isActive}>
                {props.children}
            </MenuItem>
        )

    return (
        <>
            <Container role="navigation" aria-label="Primary">
                {sidebarItems.map(({ name, to, condition, icon, openInNewTab, connentedRoute = '' }) =>
                    condition ? (
                        <>
                            <Tooltip>
                                <LinkWrapper
                                    aria-label={name}
                                    href={to}
                                    openInNewTab={openInNewTab}
                                    isActive={isAppPageActive(pathname, to, connentedRoute)}
                                >
                                    {icon}
                                    {!lg ? (
                                        <Body1
                                            fontFamily="var(--font-inter)"
                                            fontWeight={isAppPageActive(pathname, to, connentedRoute) ? 600 : 500}
                                            fontSize={17}
                                            color={
                                                isAppPageActive(asPath, to, connentedRoute)
                                                    ? theme.colors.grayScaleSecondary
                                                    : theme.colors.grayScaleSecondary
                                            }
                                        >
                                            {name}
                                        </Body1>
                                    ) : null}
                                </LinkWrapper>
                            </Tooltip>
                        </>
                    ) : null
                )}
                <MenuSpacer>
                    <div>
                        {authenticatedUser ? (
                            <ClickableMenuItem
                                onClick={toggleUserDropdown}
                                isActive={isAppPageActive(asPath, appEditProfileRoute)}
                            >
                                <ProfilePicture user={authenticatedUser} size={24} boxShadow />
                                {!lg ? (
                                    <Body1
                                        fontFamily="var(--font-inter)"
                                        fontWeight={isAppPageActive(asPath, appEditProfileRoute) ? 600 : 500}
                                        fontSize={17}
                                        color={
                                            isAppPageActive(asPath, appEditProfileRoute)
                                                ? theme.colors.grayScaleSecondary
                                                : theme.colors.grayScaleSecondary
                                        }
                                    >
                                        {tCommon('common/navBarProfileLink')}
                                    </Body1>
                                ) : null}
                            </ClickableMenuItem>
                        ) : null}
                        <UserProfileDropDown
                            open={showUserDropdown}
                            dropdownRef={userDropdownRef}
                            handleCloseDropdown={toggleUserDropdown}
                            handleLinksWithinWebapp
                            position={{
                                xOffset: 0,
                                yOffset: authenticatedKnower ? 225 : 175,
                            }}
                        />
                    </div>
                </MenuSpacer>
            </Container>
        </>
    )
}

export default SidebarNavigation
